import { AxiosError } from 'axios';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  GroupListMemberQueryDto,
  IUserRepresentation,
} from '@bom-nextgen-keycloak/models';
import {
  CONFIG,
  useAlertMessage,
  useNotification,
  useResource,
} from '@bom-nextgen-keycloak/web/core';
import {
  deleteUser,
  ErrorMessage,
  fetchgroupListMembers,
  joinGroupUser,
  QUERY_KEY,
  removeUserGroup,
} from '@bom-nextgen-keycloak/web/shared/data-access';
import {
  SearchboxEmit,
  SearchboxOptions,
} from '@bom-nextgen-keycloak/web/shared/ui';
import {
  checkProcessing,
  generateHash,
  useDebounce,
} from '@bom-nextgen-keycloak/web/shared/util';

type Arguments = {
  callback?: () => void;
  disableQuery?: boolean;
};

const useUserPage2 = (options?: Arguments) => {
  const callback = options?.callback;
  const disableQuery = options?.disableQuery;
  const enableQuery = !disableQuery;
  const resource = useResource();
  const { clientId, groupId, resourceName } = resource;
  const defaultParams = {
    first: CONFIG.pagination.offset,
    id: groupId,
    max: CONFIG.pagination.step,
  };
  const defaultData = {
    items: [],
    totalCount: 0,
    totalRecord: 0,
  };
  const defaultSelection = {
    email: '',
    id: '',
    item: null,
  };
  const queryClient = useQueryClient();
  const { setAlertMessage } = useAlertMessage();
  const { setNotification } = useNotification();
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isValidateDialogOpen, setValidateDialogOpen] = useState(false);
  const [params, setParams] = useState<GroupListMemberQueryDto>({
    ...defaultParams,
  });
  const [selectedEmail, setSelectedEmail] = useState(defaultSelection.email);
  const [selectedId, setSelectedId] = useState(defaultSelection.id);
  const [selectedItem, setSelectedItem] = useState<IUserRepresentation | null>(
    defaultSelection.item
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [currentOffset, setCurrentOffset] = useState(CONFIG.pagination.offset);
  const [previousOffset, setPreviousOffset] = useState(
    CONFIG.pagination.offset
  );
  const [pageStep, setPageStep] = useState(CONFIG.pagination.step);
  const [queryHash, setQueryHash] = useState(generateHash());
  const [isSearching, setIsSearching] = useState(false);
  const [wasSearch, setWasSearch] = useState(false);
  const debouncedSearchQuery = useDebounce(
    searchQuery,
    CONFIG.ui.debounceDelay
  );

  const isSearch = searchQuery !== '';
  const queryKeys = [
    QUERY_KEY.GROUP_MEMBERS,
    groupId,
    queryHash,
    debouncedSearchQuery,
  ];

  const {
    data = defaultData,
    isFetched,
    isFetching,
    isLoading,
  } = useQuery(
    queryKeys,
    () => {
      return fetchgroupListMembers(groupId, {
        ...params,
        // Required when resource group ID (project selector) changes since the
        // initial values of `params` state are not updated.
        id: groupId,
        search: searchQuery,
      });
    },
    {
      enabled: enableQuery,
      onError: (error: AxiosError<ErrorMessage>) => {
        const message =
          error.response?.data.message || 'Cannot get a list of users';

        setAlertMessage({ message, typeStatusMessage: 'error' });
      },
      onSuccess: () => {
        if (isSearch && isSearching) {
          setIsSearching(false);
        }

        if (wasSearch && !isSearch) {
          setWasSearch(false);
        }

        if (!isSearch) {
          setPreviousOffset(params.first);
        }
      },
      staleTime: CONFIG.query.staleTime,
    }
  );

  const isProcessing = checkProcessing({ isFetched, isFetching, isLoading });

  const addMutation = useMutation(joinGroupUser, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message || `Cannot add user to ${resourceName}`;

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: (data) => {
      setValidateDialogOpen(false);
      setAddDialogOpen(false);
      setNotification({
        message: `User added`,
        type: 'success',
      });
      handleSelectionReset();
      handleReload();
    },
  });

  const removeMutation = useMutation(removeUserGroup, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message =
        error.response?.data.message ||
        `Cannot remove user from ${resourceName}`;

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      setRemoveDialogOpen(false);
      setNotification({
        message: 'User removed',
        type: 'success',
      });

      if (enableQuery) {
        handleSelectionReset();
        handleReload();
      }

      callback && callback();
    },
  });

  const deleteMutation = useMutation(deleteUser, {
    onError: (error: AxiosError<ErrorMessage>) => {
      const message = error.response?.data.message || 'Cannot delete user';

      setAlertMessage({
        message,
        statusCode: error.response?.status,
        typeStatusMessage: 'error',
      });
    },
    onSuccess: () => {
      setDeleteDialogOpen(false);
      setNotification({
        message: 'User deleted',
        type: 'success',
      });

      if (enableQuery) {
        handleSelectionReset();
        handleReload();
      }

      callback && callback();
    },
  });

  const handleSelectionReset = () => {
    setSelectedEmail(defaultSelection.email);
    setSelectedId(defaultSelection.id);
    setSelectedItem(defaultSelection.item);
  };

  const handleRefetch = () => {
    setQueryHash(generateHash());
  };

  const handleReload = () => {
    queryClient.invalidateQueries(queryKeys);
  };

  const handleRefresh = () => {
    const offset = 0;

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));

    handleRefetch();
  };

  const handleValidateRequest = () => {
    setValidateDialogOpen(true);
  };

  const handleCreateRequest = (email: string) => {
    setValidateDialogOpen(false);

    if (email) {
      setSelectedEmail(email);
      setCreateDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleAddRequest = (id: string) => {
    if (id) {
      setSelectedId(id);
      setAddDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleRemoveRequest = (data: IUserRepresentation) => {
    if (data) {
      setSelectedItem(data);
      setRemoveDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleDeleteRequest = (data: IUserRepresentation) => {
    if (data) {
      setSelectedItem(data);
      setDeleteDialogOpen(true);
    } else {
      // TODO: Implement a handler
    }
  };

  const handleExportRequest = () => {
    setExportDialogOpen(true);
  };

  const handleCreateCancel = () => {
    setCreateDialogOpen(false);
    handleSelectionReset();
  };

  const handleAddCancel = () => {
    setAddDialogOpen(false);
    handleSelectionReset();
  };

  const handleRemoveCancel = () => {
    setRemoveDialogOpen(false);
    handleSelectionReset();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    handleSelectionReset();
  };

  const handleExportCancel = () => {
    setExportDialogOpen(false);
  };

  const handleCreateAction = () => {
    setCreateDialogOpen(false);
    handleSelectionReset();
    handleReload();
  };

  const handleAddAction = () => {
    if (selectedId) {
      addMutation.mutate({
        groupIds: [groupId],
        userId: selectedId,
      });
    } else {
      // TODO: Implement a handler
    }
  };

  const handleRemoveAction = () => {
    if (selectedItem) {
      removeMutation.mutate({
        clientId: clientId,
        groupId: groupId,
        userId: selectedItem.id,
      });
    } else {
      // TODO: Implement a handler
    }
  };

  const handleDeleteAction = () => {
    if (selectedItem) {
      deleteMutation.mutate({
        groupId: groupId,
        userId: selectedItem.id,
      });
    } else {
      // TODO: Implement a handler
    }
  };

  const handleSearch = ({ event, isEnter = false }: SearchboxOptions) => {
    const { value } = event;
    const keyword = value.trim();
    const hasKeyword = keyword !== '';
    const hasChanged = keyword !== '' && keyword !== searchQuery;
    const offset = hasKeyword ? 0 : previousOffset;

    setCurrentOffset(offset);
    setIsSearching(hasChanged);
    setParams((previous) => ({
      ...previous,
      first: offset,
      isAll: hasKeyword,
    }));

    if (isEnter && hasKeyword) {
      handleRefetch();
    } else {
      setSearchQuery((previous) => {
        setWasSearch(previous !== '' && !hasKeyword);

        return value;
      });
    }
  };

  const handleSearchChange = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: false });
  };

  const handleSearchEnter = (event: SearchboxEmit) => {
    handleSearch({ event, isEnter: true });
  };

  const handleSearchReset = () => {
    const value = '';
    const offset = previousOffset;

    setCurrentOffset(offset);
    setWasSearch(true);
    setParams((previous) => ({
      ...previous,
      first: offset,
      isAll: false,
      search: value,
    }));
    setSearchQuery(value);
  };

  const handleSearchClear = () => {
    const value = '';
    const hasKeyword = isSearch;

    if (hasKeyword) {
      handleSearchReset();
    } else {
      // NOTE: Clear whitespaces without making an API request
      setSearchQuery(value);
    }
  };

  const handlePageChange = (offset: number) => {
    if (wasSearch) {
      setWasSearch(false);
    }

    setCurrentOffset(offset);
    setParams((previous) => ({
      ...previous,
      first: offset,
    }));
    handleRefetch();
  };

  const handleStepChange = (event: SelectChangeEvent<unknown>) => {
    const step = event.target.value as number;
    const offset = 0;

    setCurrentOffset(offset);
    setPreviousOffset(offset);
    setPageStep(step);
    setParams((previous) => ({
      ...previous,
      first: offset,
      max: step,
    }));
    handleRefetch();
  };

  return {
    data,
    handleAddAction,
    handleAddCancel,
    handleAddRequest,
    handleCreateAction,
    handleCreateCancel,
    handleCreateRequest,
    handleDeleteAction,
    handleDeleteCancel,
    handleDeleteRequest,
    handleExportCancel,
    handleExportRequest,
    handlePageChange,
    handleRefresh,
    handleRemoveAction,
    handleRemoveCancel,
    handleRemoveRequest,
    handleSearchChange,
    handleSearchClear,
    handleSearchEnter,
    handleSearchReset,
    handleStepChange,
    handleValidateRequest,
    isAddDialogOpen,
    isAdding: addMutation.isLoading,
    isFetched,
    isFetching,
    isCreateDialogOpen,
    isDeleteDialogOpen,
    isDeleting: deleteMutation.isLoading,
    isExportDialogOpen,
    isLoading,
    isProcessing,
    isRemoveDialogOpen,
    isRemoving: removeMutation.isLoading,
    isSearching,
    isValidateDialogOpen,
    paginationOffset: currentOffset,
    paginationStep: pageStep,
    resource,
    searchQuery,
    selectedEmail,
    selectedItem,
    wasSearch,
  };
};

export { useUserPage2 };
